import { MapPin, Battery, Bug, BellRing, Rocket, Check, Loader2, type LucideIcon } from 'lucide-react';
import { useState } from 'react';
import { useDriverTheme } from '@/hooks/useDriverTheme';
import {
  usePermissionsGate,
  type PermissionKey,
  type PermissionsState,
} from '@/hooks/usePermissionsGate';
import BackgroundLocationRationaleSheet from '@/components/BackgroundLocationRationaleSheet';

/**
 * Full-screen Required Permissions page.
 * Compact single-screen layout — all four cards fit on a 390×844 viewport
 * without scrolling. Replaces the entire app surface (including login)
 * until every required permission is satisfied.
 */

interface CardConfig {
  key: PermissionKey;
  title: string;
  body: string;
  icon: LucideIcon;
}

const CARDS: CardConfig[] = [
  {
    key: 'location',
    title: 'Location',
    body: 'Find nearby bookings and run the trip meter — needs all-the-time access.',
    icon: MapPin,
  },
  {
    key: 'battery',
    title: 'Ignore Battery Optimisation',
    body: 'Stay active in the background so trip alerts always reach you.',
    icon: Battery,
  },
  {
    key: 'developer',
    title: 'Developer Option',
    body: 'Keep this OFF to block fake-GPS tools from spoofing your location.',
    icon: Bug,
  },
  {
    key: 'fullScreen',
    title: 'Full-Screen Trip Alerts',
    body: 'Allow trip offers to pop up over the lock screen so you never miss a ride.',
    icon: BellRing,
  },
  {
    key: 'oemAutostart',
    title: 'Autostart',
    body: 'Allow the app to auto-start so trip alerts still arrive after the phone restarts or the app is closed.',
    icon: Rocket,
  },
];

function isGranted(perms: PermissionsState, key: PermissionKey): boolean {
  return perms[key] === 'granted';
}

export default function RequiredPermissionsScreen() {
  const { tokens, isDark } = useDriverTheme();
  const { perms, request } = usePermissionsGate();
  const [busy, setBusy] = useState<PermissionKey | null>(null);

  const handleClick = async (key: PermissionKey) => {
    if (busy) return;
    setBusy(key);
    try {
      await request(key);
    } finally {
      setBusy(null);
    }
  };

  const cardBorder = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';
  const iconBg = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)';
  const cardBg = isDark ? '#241c17' : '#ffffff';

  return (
    <div
      className="fixed inset-0 z-[60] flex flex-col"
      style={{
        background: isDark ? '#1a1410' : '#f3f3f3',
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
    >
      <div
        className="px-4 py-2.5 text-center text-sm font-bold border-b shrink-0"
        style={{
          background: tokens.bgPage,
          color: tokens.textPrimary,
          borderColor: cardBorder,
        }}
      >
        Required Permission
      </div>

      <div className="flex flex-col gap-2.5 px-3 py-3">
        {CARDS.map((card) => {
          const granted = isGranted(perms, card.key);
          const Icon = card.icon;
          return (
            <div
              key={card.key}
              className="rounded-xl px-3 py-3 shadow-sm flex items-center gap-3"
              style={{
                background: cardBg,
                border: `1px solid ${cardBorder}`,
              }}
            >
              <div
                className="flex h-9 w-9 items-center justify-center rounded-full shrink-0"
                style={{ background: iconBg }}
              >
                <Icon className="h-4 w-4" style={{ color: tokens.textPrimary }} />
              </div>

              <div className="flex-1 min-w-0">
                <h3
                  className="text-sm font-bold leading-tight"
                  style={{ color: tokens.textPrimary }}
                >
                  {card.title}
                </h3>
                <p
                  className="text-[11px] leading-snug mt-0.5"
                  style={{ color: tokens.textSecondary }}
                >
                  {card.body}
                </p>
              </div>

              <div className="shrink-0">
                {granted ? (
                  <div
                    className="flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-full"
                    style={{
                      color: '#16a34a',
                      background: isDark ? 'rgba(34,197,94,0.12)' : 'rgba(34,197,94,0.10)',
                    }}
                  >
                    <Check className="h-3 w-3" strokeWidth={3} />
                    Granted
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleClick(card.key)}
                    disabled={busy !== null}
                    className="text-[11px] font-bold tracking-wide px-3 py-1.5 rounded-full flex items-center gap-1 disabled:opacity-50"
                    style={{
                      color: '#ffffff',
                      background: '#dc2626',
                    }}
                  >
                    {busy === card.key && <Loader2 className="h-3 w-3 animate-spin" />}
                    Allow
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <BackgroundLocationRationaleSheet />
    </div>
  );
}
